import {DymekEvidenceClass} from './evidence-policy.js';

function instant(value){
  const parsed=Date.parse(String(value??''));
  return Number.isNaN(parsed)?null:new Date(parsed).toISOString();
}
function number(value){
  const n=Number(value);
  return Number.isFinite(n)?n:null;
}
function text(value,max=240){
  const result=String(value??'').trim();
  return result?result.slice(0,max):null;
}
function completedAt(bundle){
  return instant(
    bundle?.session?.actualEndAt
    ||bundle?.session?.statusChangedAt
    ||bundle?.session?.updatedAt
  );
}
function sessionRef(bundle){
  const id=String(bundle?.session?.id||'').trim();
  const at=completedAt(bundle);
  if(!id||!at)throw new TypeError('Completed history session requires id and completion date');
  return Object.freeze({kind:'session',ref:id,observedAt:at});
}
function observed(content,bundles){
  return Object.freeze({
    content,
    evidenceClass:DymekEvidenceClass.OBSERVED,
    safetyCritical:false,
    historical:true,
    evidenceRefs:Object.freeze(bundles.map(sessionRef)),
    assumptions:Object.freeze([])
  });
}
function suggestion(content,assumptions=[]){
  return Object.freeze({
    content,
    evidenceClass:DymekEvidenceClass.GENERAL_SUGGESTION,
    safetyCritical:false,
    historical:false,
    evidenceRefs:Object.freeze([]),
    assumptions:Object.freeze(assumptions)
  });
}
function stageRows(bundle){
  const targets=new Map((bundle?.stageTargets||[]).map(row=>[
    String(row.stageId||row.recipeStageId||''),row
  ]));
  return [...(bundle?.stages||[])]
    .filter(stage=>!stage?.deletedAt)
    .sort((a,b)=>Number(a?.sortOrder??a?.orderIndex??a?.position??0)
      -Number(b?.sortOrder??b?.orderIndex??b?.position??0))
    .map(stage=>{
      const target=targets.get(String(stage.id))||{};
      return Object.freeze({
        typeKey:text(stage.typeKey||stage.type_key,60),
        label:text(stage.nameOverride||stage.name||stage.typeKey||stage.type_key,120),
        plannedDurationMinutes:number(target.plannedDurationMinutes??target.planned_duration_minutes),
        targetChamberCelsius:number(target.targetChamberCelsius??target.target_chamber_c)
      });
    });
}
function woodKeys(bundle){
  return Object.freeze((bundle?.woods||[])
    .filter(row=>!row?.deletedAt)
    .map(row=>text(row.woodKey||row.wood_key||row.nameSnapshot||row.woodName,80))
    .filter(Boolean)
    .slice(0,8));
}
function outcome(bundle,includePrivateText=false){
  const row=bundle?.outcome||{};
  return Object.freeze({
    overallRating:number(row.overallRating??row.overall_rating),
    taste:number(row.taste??row.taste_rating),
    smoke:number(row.smoke??row.smoke_rating),
    juiciness:number(row.juiciness??row.juiciness_rating),
    texture:number(row.texture??row.texture_rating),
    appearance:number(row.appearance??row.appearance_rating),
    ...(includePrivateText?{
      lessonsLearned:text(row.lessonsLearned||row.lessons_learned,1200)
    }:{})
  });
}

export function historySessionChoice(bundle,{includePrivateText=false}={}){
  if(bundle?.session?.status!=='completed')throw new TypeError('History comparison requires completed session');
  return Object.freeze({
    id:String(bundle.session.id),
    completedAt:completedAt(bundle),
    title:text(bundle.session.title,160),
    stages:Object.freeze(stageRows(bundle)),
    woods:woodKeys(bundle),
    outcome:outcome(bundle,includePrivateText)
  });
}

export function compareDymekHistory(leftBundle,rightBundle,{
  includePrivateText=false
}={}){
  const left=historySessionChoice(leftBundle,{includePrivateText});
  const right=historySessionChoice(rightBundle,{includePrivateText});
  if(left.id===right.id)throw new TypeError('Choose two different history sessions');

  const claims=[];
  if(left.outcome.overallRating!=null||right.outcome.overallRating!=null){
    claims.push(observed(
      'Ocena ogólna: '+(left.outcome.overallRating??'brak')+' vs '+(right.outcome.overallRating??'brak')+'.',
      [leftBundle,rightBundle]
    ));
  }

  const max=Math.max(left.stages.length,right.stages.length);
  const stageDiffs=[];
  for(let i=0;i<max;i+=1){
    const a=left.stages[i],b=right.stages[i];
    if(!a||!b){
      stageDiffs.push('liczba etapów '+left.stages.length+' vs '+right.stages.length);
      break;
    }
    if(a.typeKey!==b.typeKey){
      stageDiffs.push('etap '+(i+1)+': '+(a.label||a.typeKey)+' vs '+(b.label||b.typeKey));
      continue;
    }
    if(a.plannedDurationMinutes!==b.plannedDurationMinutes){
      stageDiffs.push((a.label||a.typeKey)+': czas '+(a.plannedDurationMinutes??'brak')+' vs '+(b.plannedDurationMinutes??'brak')+' min');
    }
    if(a.targetChamberCelsius!==b.targetChamberCelsius){
      stageDiffs.push((a.label||a.typeKey)+': cel '+(a.targetChamberCelsius??'brak')+' vs '+(b.targetChamberCelsius??'brak')+'°C');
    }
  }
  if(stageDiffs.length){
    claims.push(observed('Różnice procesu: '+stageDiffs.slice(0,5).join('; ')+'.',[leftBundle,rightBundle]));
  }

  if(JSON.stringify(left.woods)!==JSON.stringify(right.woods)){
    claims.push(observed(
      'Drewno: '+(left.woods.join(', ')||'brak zapisu')+' vs '+(right.woods.join(', ')||'brak zapisu')+'.',
      [leftBundle,rightBundle]
    ));
  }

  if(includePrivateText&&(left.outcome.lessonsLearned||right.outcome.lessonsLearned)){
    claims.push(observed(
      'Wnioski zapisane po sesjach: „'+(left.outcome.lessonsLearned||'brak')+'” vs „'+(right.outcome.lessonsLearned||'brak')+'”.',
      [leftBundle,rightBundle]
    ));
  }

  claims.push(suggestion(
    'Te różnice pokazują korelację między próbami, ale pojedyncze porównanie nie dowodzi, że któraś zmiana spowodowała wynik.',
    ['Porównanie dwóch sesji nie pozwala wiarygodnie ustalić przyczynowości.']
  ));

  return Object.freeze({
    left,right,
    claims:Object.freeze(claims),
    links:Object.freeze([
      Object.freeze({kind:'session',ref:left.id,label:'Otwórz wcześniejszą sesję'}),
      Object.freeze({kind:'session',ref:right.id,label:'Otwórz drugą sesję'})
    ])
  });
}

export function chooseComparableHistory(historyBundles=[],{
  currentSessionId=null,
  limit=5
}={}){
  const rows=(historyBundles||[])
    .filter(bundle=>bundle?.session?.status==='completed')
    .filter(bundle=>String(bundle.session.id)!==String(currentSessionId||''))
    .sort((a,b)=>Date.parse(completedAt(b)||0)-Date.parse(completedAt(a)||0))
    .slice(0,Math.max(0,Number(limit)||0))
    .map(bundle=>historySessionChoice(bundle));
  return Object.freeze(rows);
}
